import React, { useEffect, useMemo, useState } from "react";
import Store from "../../store/Store";

type ResultTypeDto = {
  id: number;
  name: string;
  description?: string | null;
  purposeId?: number | null;
  purposeName?: string | null;
  missionId?: number | null;
  missionName?: string | null;
};

function resultTypeHierarchyLabel(rt: ResultTypeDto): string {
  if (rt.missionName && rt.purposeName) {
    return `${rt.missionName} > ${rt.purposeName} > ${rt.name}`;
  }
  return rt.name;
}
type ResultTypeEdit = { id?: number; name: string; description?: string | null };
type PurposeDto = {
  id?: number;
  name: string;
  description?: string | null;
  image?: string | null;
  resultTypes?: ResultTypeEdit[];
};
type MissionDto = {
  id: number;
  name: string;
  description?: string | null;
  icon?: string | null;
  purposes?: PurposeDto[];
};

type PresetDto = {
  id: number;
  name: string;
  description?: string | null;
  analysis?: string | null;
  price?: number | null;
  custom?: boolean | null;
  icon?: string | null;
  resultTypes?: ResultTypeDto[];
};

type MasterdataResponse = {
  missions: MissionDto[];
  presets: PresetDto[];
};

const ConfirmModal: React.FC<{
  title: string;
  message: string;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ title, message, onCancel, onConfirm }) => (
  <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
    <div className="bg-white p-6 rounded-xl shadow-xl w-full max-w-sm">
      <h2 className="text-lg font-semibold mb-3">{title}</h2>
      <p className="text-gray-700 mb-6 whitespace-pre-line">{message}</p>
      <div className="flex justify-end space-x-3">
        <button onClick={onCancel} className="px-4 py-2 rounded border">
          Cancel
        </button>
        <button
          onClick={onConfirm}
          className="bg-red-600 text-white px-4 py-2 rounded shadow hover:bg-red-700"
        >
          Delete
        </button>
      </div>
    </div>
  </div>
);

const MissionModal: React.FC<{
  title: string;
  onClose: () => void;
  onSubmit: (mission: {
    name: string;
    description?: string;
    purposes: Array<{
      id?: number;
      name: string;
      description?: string;
      resultTypes: Array<{ id?: number; name: string; description?: string }>;
    }>;
  }) => void;
  onUploadIcon?: (file: File) => Promise<void>;
  onDeleteIcon?: () => Promise<void>;
  onUploadPurposeImage?: (purposeId: number, file: File) => Promise<void>;
  onDeletePurposeImage?: (purposeId: number) => Promise<void>;
  mission?: MissionDto;
}> = ({ title, onClose, onSubmit, mission, onUploadIcon, onDeleteIcon, onUploadPurposeImage, onDeletePurposeImage }) => {
  const [name, setName] = useState(mission?.name ?? "");
  const [description, setDescription] = useState(mission?.description ?? "");
  const [purposes, setPurposes] = useState<PurposeDto[]>(
    mission?.purposes ? JSON.parse(JSON.stringify(mission.purposes)) : []
  );
  const [error, setError] = useState<string | null>(null);
  const [iconUploading, setIconUploading] = useState(false);
  const [purposeUploadingId, setPurposeUploadingId] = useState<number | null>(null);

  useEffect(() => {
    if (!mission?.purposes?.length) return;
    setPurposes((prev) =>
      prev.map((p) => {
        if (p.id == null) return p;
        const srv = mission.purposes!.find((sp) => sp.id === p.id);
        if (!srv || srv.image === p.image) return p;
        return { ...p, image: srv.image };
      })
    );
  }, [mission]);

  const addPurpose = () => {
    setPurposes((p) => [
      ...p,
      { name: "", description: "", image: "", resultTypes: [] },
    ]);
  };

  const deletePurpose = (idx: number) => {
    setPurposes((p) => p.filter((_, i) => i !== idx));
  };

  const updatePurpose = (idx: number, patch: Partial<PurposeDto>) => {
    setPurposes((p) => p.map((e, i) => (i === idx ? { ...e, ...patch } : e)));
  };

  const addResultType = (pIdx: number) => {
    setPurposes((p) =>
      p.map((pp, i) =>
        i !== pIdx
          ? pp
          : {
              ...pp,
              resultTypes: [...(pp.resultTypes ?? []), { name: "", description: "" }],
            }
      )
    );
  };

  const deleteResultType = (pIdx: number, rtIdx: number) => {
    setPurposes((p) =>
      p.map((pp, i) =>
        i !== pIdx
          ? pp
          : { ...pp, resultTypes: (pp.resultTypes ?? []).filter((_, j) => j !== rtIdx) }
      )
    );
  };

  const updateResultType = (
    pIdx: number,
    rtIdx: number,
    patch: Partial<ResultTypeEdit>
  ) => {
    setPurposes((p) =>
      p.map((pp, i) =>
        i !== pIdx
          ? pp
          : {
              ...pp,
              resultTypes: (pp.resultTypes ?? []).map((rt, j) =>
                j === rtIdx ? { ...rt, ...patch } : rt
              ),
            }
      )
    );
  };

  const handleSubmit = () => {
    const errors: string[] = [];
    if (!name.trim()) errors.push("Mission name is required");

    purposes.forEach((p, idx) => {
      if (!p.name?.trim()) errors.push(`Purpose #${idx + 1}: name is required`);
      (p.resultTypes ?? []).forEach((rt, rti) => {
        if (!rt.name?.trim()) errors.push(`Purpose #${idx + 1}, result type #${rti + 1}: name is required`);
      });
    });

    if (errors.length) {
      setError(errors.join("\n"));
      return;
    }

    onSubmit({
      name: name.trim(),
      description: description.trim() || undefined,
      purposes: purposes.map((p) => ({
        id: p.id,
        name: p.name.trim(),
        description: (p.description ?? "").trim() || undefined,
        resultTypes: (p.resultTypes ?? []).map((rt) => ({
          id: rt.id,
          name: rt.name.trim(),
          description: (rt.description ?? "").trim() || undefined,
        })),
      })),
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
      <div className="bg-white p-6 rounded-xl shadow-xl w-full max-w-5xl max-h-[85vh] overflow-auto">
        <h2 className="text-xl font-semibold mb-4">{title}</h2>

        {error && (
          <div className="bg-red-100 text-red-700 p-2 mb-3 rounded whitespace-pre-line">
            {error}
          </div>
        )}

        <div className="grid grid-cols-3 gap-3">
          <input
            className="border p-2 rounded col-span-1"
            placeholder="Mission name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <input
            className="border p-2 rounded col-span-3"
            placeholder="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div className="mt-3 flex items-center gap-4">
          <div className="font-semibold">Icon</div>
          {mission?.icon ? (
            <img
              src={Store.config.api.url.replace(/\/?$/, "/") + mission.icon.replace(/^\//, "")}
              alt="mission icon"
              className="h-10 w-10 object-contain border rounded bg-white"
            />
          ) : (
            <div className="text-sm text-gray-500">No icon</div>
          )}

          <input
            type="file"
            accept="image/*"
            disabled={!mission?.id || !onUploadIcon || iconUploading}
            onChange={async (e) => {
              const f = e.target.files?.[0];
              if (!f || !onUploadIcon) return;
              setIconUploading(true);
              try {
                await onUploadIcon(f);
              } finally {
                setIconUploading(false);
                e.target.value = "";
              }
            }}
          />
          <button
            className="px-3 py-1 rounded border bg-white hover:bg-gray-100 disabled:opacity-40"
            disabled={!mission?.id || !mission?.icon || !onDeleteIcon || iconUploading}
            onClick={() => onDeleteIcon?.()}
          >
            Remove
          </button>
          {iconUploading && <span className="text-sm text-gray-500">Uploading...</span>}
          {!mission?.id && (
            <span className="text-sm text-gray-500">Save mission first to upload icon.</span>
          )}
        </div>

        <div className="mt-5 flex items-center justify-between">
          <h3 className="text-lg font-semibold">Purposes</h3>
          <button
            onClick={addPurpose}
            className="bg-blue-600 text-white px-4 py-2 rounded-xl shadow hover:bg-blue-700 transition"
          >
            + Add purpose
          </button>
        </div>

        <div className="mt-3 flex flex-col gap-4">
          {purposes.map((p, idx) => (
            <div key={idx} className="border rounded-xl p-4 bg-gray-50">
              <div className="flex items-center justify-between gap-3">
                <h4 className="font-semibold">Purpose #{idx + 1}</h4>
                <button
                  onClick={() => deletePurpose(idx)}
                  className="text-red-600 hover:text-red-800 font-medium"
                >
                  Delete purpose
                </button>
              </div>

              <div className="grid grid-cols-3 gap-3 mt-3">
                <input
                  className="border p-2 rounded col-span-1"
                  placeholder="Purpose name"
                  value={p.name ?? ""}
                  onChange={(e) => updatePurpose(idx, { name: e.target.value })}
                />
                <input
                  className="border p-2 rounded col-span-3"
                  placeholder="Purpose description"
                  value={p.description ?? ""}
                  onChange={(e) => updatePurpose(idx, { description: e.target.value })}
                />
              </div>

              <div className="mt-3 flex items-center gap-4">
                <div className="font-semibold">Image</div>
                {p.image ? (
                  <img
                    src={Store.config.api.url.replace(/\/?$/, "/") + p.image.replace(/^\//, "")}
                    alt="purpose"
                    className="h-10 w-10 object-cover border rounded bg-white"
                  />
                ) : (
                  <div className="text-sm text-gray-500">No image</div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  disabled={!p.id || !onUploadPurposeImage || purposeUploadingId === p.id}
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (!f || !p.id || !onUploadPurposeImage) return;
                    setPurposeUploadingId(p.id);
                    try {
                      await onUploadPurposeImage(p.id, f);
                    } finally {
                      setPurposeUploadingId(null);
                      e.target.value = "";
                    }
                  }}
                />
                <button
                  className="px-3 py-1 rounded border bg-white hover:bg-gray-100 disabled:opacity-40"
                  disabled={!p.id || !p.image || !onDeletePurposeImage || purposeUploadingId === p.id}
                  onClick={() => p.id && onDeletePurposeImage?.(p.id)}
                >
                  Remove
                </button>
                {!p.id && (
                  <div className="text-sm text-gray-500">Save mission first (to create purpose id) then upload.</div>
                )}
                {p.id && purposeUploadingId === p.id && (
                  <div className="text-sm text-gray-500">Uploading...</div>
                )}
              </div>

              <div className="mt-4 flex items-center justify-between">
                <h5 className="font-semibold">Result types</h5>
                <button
                  onClick={() => addResultType(idx)}
                  className="px-3 py-1 rounded border bg-white hover:bg-gray-100"
                >
                  + Add result type
                </button>
              </div>

              <div className="mt-2 flex flex-col gap-2">
                {(p.resultTypes ?? []).map((rt, rtIdx) => (
                  <div key={rtIdx} className="grid grid-cols-3 gap-3 items-center">
                    <input
                      className="border p-2 rounded col-span-1"
                      placeholder="Result type name"
                      value={rt.name ?? ""}
                      onChange={(e) => updateResultType(idx, rtIdx, { name: e.target.value })}
                    />
                    <input
                      className="border p-2 rounded col-span-2"
                      placeholder="Result type description"
                      value={rt.description ?? ""}
                      onChange={(e) => updateResultType(idx, rtIdx, { description: e.target.value })}
                    />
                    <div className="col-span-3 flex justify-end">
                      <button
                        onClick={() => deleteResultType(idx, rtIdx)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Delete result type
                      </button>
                    </div>
                  </div>
                ))}
                {(p.resultTypes ?? []).length === 0 && (
                  <div className="text-gray-500 text-sm">No result types.</div>
                )}
              </div>
            </div>
          ))}
          {purposes.length === 0 && (
            <div className="text-gray-500 text-sm">No purposes yet.</div>
          )}
        </div>

        <div className="flex justify-end mt-6 space-x-3">
          <button onClick={onClose} className="px-4 py-2 rounded border">
            Cancel
          </button>
          <button
            className="bg-blue-600 text-white px-4 py-2 rounded shadow hover:bg-blue-700"
            onClick={handleSubmit}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

const PresetModal: React.FC<{
  title: string;
  onClose: () => void;
  onSubmit: (preset: {
    name: string;
    description?: string;
    analysis?: string;
    price?: number;
    custom?: boolean;
    resultTypeIds: number[];
  }) => void;
  preset?: PresetDto;
  allResultTypes: ResultTypeDto[];
  onUploadIcon?: (file: File) => Promise<void>;
  onDeleteIcon?: () => Promise<void>;
}> = ({ title, onClose, onSubmit, preset, allResultTypes, onUploadIcon, onDeleteIcon }) => {
  const [name, setName] = useState(preset?.name ?? "");
  const [description, setDescription] = useState(preset?.description ?? "");
  const [analysis, setAnalysis] = useState(preset?.analysis ?? "");
  const [price, setPrice] = useState(preset?.price ?? 0);
  const [custom, setCustom] = useState(preset?.custom ?? false);
  const [selectedIds, setSelectedIds] = useState<number[]>(
    (preset?.resultTypes ?? []).map((rt) => rt.id)
  );
  const [error, setError] = useState<string | null>(null);
  const [iconUploading, setIconUploading] = useState(false);

  const toggleId = (id: number) => {
    setSelectedIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  };

  const handleSubmit = () => {
    const errors: string[] = [];
    if (!name.trim()) errors.push("Preset name is required");
    if (errors.length) {
      setError(errors.join("\n"));
      return;
    }
    onSubmit({
      name: name.trim(),
      description: description.trim() || undefined,
      analysis: analysis.trim() || undefined,
      price: price || undefined,
      custom: custom,
      resultTypeIds: selectedIds,
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
      <div className="bg-white p-6 rounded-xl shadow-xl w-full max-w-3xl max-h-[85vh] overflow-auto">
        <h2 className="text-xl font-semibold mb-4">{title}</h2>

        {error && (
          <div className="bg-red-100 text-red-700 p-2 mb-3 rounded whitespace-pre-line">
            {error}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <input className="border p-2 rounded" placeholder="Preset name" value={name} onChange={(e) => setName(e.target.value)} />
          <input className="border p-2 rounded col-span-2" placeholder="Description" value={description} onChange={(e) => setDescription(e.target.value)} />
          <input className="border p-2 rounded col-span-2" placeholder="Analysis" value={analysis} onChange={(e) => setAnalysis(e.target.value)} />
          <input className="border p-2 rounded col-span-2" placeholder="Price" value={price} onChange={(e) => setPrice(Number(e.target.value))} />
          <input type="checkbox" className="border p-2 rounded" checked={custom} onChange={(e) => setCustom(e.target.checked)} />
          <label className="text-sm text-gray-500">Custom</label>
        </div>

        <div className="mt-3 flex items-center gap-4">
          <div className="font-semibold">Icon</div>
          {preset?.icon ? (
            <img
              src={Store.config.api.url.replace(/\/?$/, "/") + preset.icon.replace(/^\//, "")}
              alt="preset icon"
              className="h-10 w-10 object-contain border rounded bg-white"
            />
          ) : (
            <div className="text-sm text-gray-500">No icon</div>
          )}

          <input
            type="file"
            accept="image/*"
            disabled={!preset?.id || !onUploadIcon || iconUploading}
            onChange={async (e) => {
              const f = e.target.files?.[0];
              if (!f || !onUploadIcon) return;
              setIconUploading(true);
              try {
                await onUploadIcon(f);
              } finally {
                setIconUploading(false);
                e.target.value = "";
              }
            }}
          />
          <button
            className="px-3 py-1 rounded border bg-white hover:bg-gray-100 disabled:opacity-40"
            disabled={!preset?.id || !preset?.icon || !onDeleteIcon || iconUploading}
            onClick={() => onDeleteIcon?.()}
          >
            Remove
          </button>
          {iconUploading && <span className="text-sm text-gray-500">Uploading...</span>}
          {!preset?.id && (
            <span className="text-sm text-gray-500">Save preset first to upload icon.</span>
          )}
        </div>

        <div className="mt-5">
          <h3 className="text-lg font-semibold mb-2">Result types in this preset</h3>
          <div className="grid grid-cols-2 gap-2">
            {allResultTypes.map((rt) => (
              <label key={rt.id} className="flex items-start gap-2 border rounded p-2 bg-gray-50">
                <input
                  type="checkbox"
                  className="mt-1 shrink-0"
                  checked={selectedIds.includes(rt.id)}
                  onChange={() => toggleId(rt.id)}
                />
                <div>
                  <div className="font-medium leading-snug">{resultTypeHierarchyLabel(rt)}</div>
                  {rt.description ? (
                    <div className="text-xs text-gray-600 mt-0.5">({rt.description})</div>
                  ) : null}
                </div>
              </label>
            ))}
          </div>
          {allResultTypes.length === 0 && (
            <div className="text-gray-500 text-sm">No result types available yet.</div>
          )}
        </div>

        <div className="flex justify-end mt-6 space-x-3">
          <button onClick={onClose} className="px-4 py-2 rounded border">Cancel</button>
          <button onClick={handleSubmit} className="bg-blue-600 text-white px-4 py-2 rounded shadow hover:bg-blue-700">Save</button>
        </div>
      </div>
    </div>
  );
};

const DataManagementPage: React.FC = () => {
  const [tab, setTab] = useState<"missions" | "presets">("missions");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<MasterdataResponse>({ missions: [], presets: [] });

  const [showMissionModal, setShowMissionModal] = useState(false);
  const [showPresetModal, setShowPresetModal] = useState(false);
  const [editingMission, setEditingMission] = useState<MissionDto | undefined>(undefined);
  const [editingPreset, setEditingPreset] = useState<PresetDto | undefined>(undefined);
  const [confirm, setConfirm] = useState<null | { kind: "mission" | "preset"; id: number; name: string }>(null);

  const allResultTypes = useMemo(() => {
    const map = new Map<number, ResultTypeDto>();
    data.missions.forEach((m) =>
      (m.purposes ?? []).forEach((p) =>
        (p.resultTypes ?? []).forEach((rt) => {
          if (rt.id != null) {
            map.set(rt.id, rt as ResultTypeDto);
          }
        })
      )
    );
    return Array.from(map.values()).sort((a, b) => {
      const ma = a.missionName ?? "";
      const mb = b.missionName ?? "";
      if (ma !== mb) return ma.localeCompare(mb);
      const pa = a.purposeName ?? "";
      const pb = b.purposeName ?? "";
      if (pa !== pb) return pa.localeCompare(pb);
      return a.name.localeCompare(b.name);
    });
  }, [data]);

  const load = async (): Promise<MasterdataResponse | null> => {
    setLoading(true);
    setError(null);
    const res = await Store.fetch("admin/masterdata");
    if (!res) {
      setError("Failed to load masterdata");
      setLoading(false);
      return null;
    }
    setData(res);
    setLoading(false);
    return res;
  };

  useEffect(() => {
    load();
  }, []);

  const saveMission = async (payload: any) => {
    const isEdit = !!editingMission?.id;
    const url = isEdit ? `admin/masterdata/missions/${editingMission!.id}` : "admin/masterdata/missions";
    const method = isEdit ? "PUT" : "POST";
    const res = await Store.fetch(url, { method, body: JSON.stringify(payload) });
    if (res) {
      setShowMissionModal(false);
      setEditingMission(undefined);
      await load();
      Store.masterdata = await Store.fetch('masterdata');
    }
  };

  const uploadMissionIcon = async (missionId: number, file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await Store.fetch(`admin/masterdata/missions/${missionId}/icon`, {
      method: "PUT",
      body: fd,
    });
    if (res) {
      const fresh = await load();
      Store.masterdata = await Store.fetch("masterdata");
      if (fresh) {
        setEditingMission((prev) => {
          if (!prev || prev.id !== missionId) return prev;
          return fresh.missions.find((x) => x.id === missionId) ?? prev;
        });
      }
    }
  };

  const deleteMissionIcon = async (missionId: number) => {
    const ok = await Store.fetch(`admin/masterdata/missions/${missionId}/icon`, {
      method: "DELETE",
    });
    if (ok) {
      const fresh = await load();
      Store.masterdata = await Store.fetch("masterdata");
      if (fresh) {
        setEditingMission((prev) => {
          if (!prev || prev.id !== missionId) return prev;
          return fresh.missions.find((x) => x.id === missionId) ?? prev;
        });
      }
    }
  };

  const uploadPurposeImage = async (purposeId: number, file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await Store.fetch(`admin/masterdata/purposes/${purposeId}/image`, {
      method: "PUT",
      body: fd,
    });
    if (res) {
      const fresh = await load();
      Store.masterdata = await Store.fetch("masterdata");
      if (fresh) {
        setEditingMission((prev) => {
          if (!prev) return prev;
          return fresh.missions.find((x) => x.id === prev.id) ?? prev;
        });
      }
    }
  };

  const deletePurposeImage = async (purposeId: number) => {
    const ok = await Store.fetch(`admin/masterdata/purposes/${purposeId}/image`, {
      method: "DELETE",
    });
    if (ok) {
      const fresh = await load();
      Store.masterdata = await Store.fetch("masterdata");
      if (fresh) {
        setEditingMission((prev) => {
          if (!prev) return prev;
          return fresh.missions.find((x) => x.id === prev.id) ?? prev;
        });
      }
    }
  };

  const deleteMission = async (id: number) => {
    const ok = await Store.fetch(`admin/masterdata/missions/${id}`, { method: "DELETE" });
    if (ok) {
      await load();
      Store.masterdata = await Store.fetch('masterdata');
    }
  };

  const savePreset = async (payload: any) => {
    const isEdit = !!editingPreset?.id;
    const url = isEdit ? `admin/masterdata/presets/${editingPreset!.id}` : "admin/masterdata/presets";
    const method = isEdit ? "PUT" : "POST";
    const res = await Store.fetch(url, { method, body: JSON.stringify(payload) });
    if (res) {
      setShowPresetModal(false);
      setEditingPreset(undefined);
      await load();
      Store.masterdata = await Store.fetch('masterdata');
    }
  };

  const uploadPresetIcon = async (presetId: number, file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await Store.fetch(`admin/masterdata/presets/${presetId}/icon`, {
      method: "PUT",
      body: fd,
    });
    if (res) {
      const fresh = await load();
      Store.masterdata = await Store.fetch("masterdata");
      if (fresh) {
        setEditingPreset((prev) => {
          if (!prev || prev.id !== presetId) return prev;
          return fresh.presets.find((x) => x.id === presetId) ?? prev;
        });
      }
    }
  };

  const deletePresetIcon = async (presetId: number) => {
    const ok = await Store.fetch(`admin/masterdata/presets/${presetId}/icon`, {
      method: "DELETE",
    });
    if (ok) {
      const fresh = await load();
      Store.masterdata = await Store.fetch("masterdata");
      if (fresh) {
        setEditingPreset((prev) => {
          if (!prev || prev.id !== presetId) return prev;
          return fresh.presets.find((x) => x.id === presetId) ?? prev;
        });
      }
    }
  };

  const deletePreset = async (id: number) => {
    const ok = await Store.fetch(`admin/masterdata/presets/${id}`, { method: "DELETE" });
    if (ok) {
      await load();
      Store.masterdata = await Store.fetch('masterdata');
    }
  };

  return (
    <div className="mx-4 rounded-2xl p-6 overflow-auto">
      <div className="flex gap-x-6 items-center mb-4">
        <h1 className="text-2xl font-semibold text-gray-800">Data Management</h1>
        <div className="flex gap-2">
          <button
            className={`px-4 py-2 rounded-xl border ${tab === "missions" ? "bg-blue-600 text-white" : "bg-white"}`}
            onClick={() => setTab("missions")}
          >
            Missions
          </button>
          <button
            className={`px-4 py-2 rounded-xl border ${tab === "presets" ? "bg-blue-600 text-white" : "bg-white"}`}
            onClick={() => setTab("presets")}
          >
            Presets
          </button>
        </div>

        <div>
          {tab === "missions" ? (
            <button
              className="bg-blue-600 text-white px-4 py-2 rounded-xl shadow hover:bg-blue-700 transition"
              onClick={() => { setEditingMission(undefined); setShowMissionModal(true); }}
            >
              + Create Mission
            </button>
          ) : (
            <button
              className="bg-blue-600 text-white px-4 py-2 rounded-xl shadow hover:bg-blue-700 transition"
              onClick={() => { setEditingPreset(undefined); setShowPresetModal(true); }}
            >
              + Create Preset
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <p className="text-gray-500">Loading...</p>
      ) : error ? (
        <p className="text-red-500">{error}</p>
      ) : tab === "missions" ? (
        <table className="min-w-full border-collapse bg-white shadow rounded-xl overflow-hidden">
          <thead>
            <tr className="bg-gray-100 text-left text-gray-600 text-sm uppercase tracking-wider">
              <th className="py-3 px-4">Name</th>
              <th className="py-3 px-4">Purposes</th>
              <th className="py-3 px-4">Result types</th>
              <th className="py-3 px-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.missions.map((m) => {
              const purposeCount = (m.purposes ?? []).length;
              const rtCount = (m.purposes ?? []).reduce((acc, p) => acc + ((p.resultTypes ?? []).length), 0);
              return (
                <tr key={m.id} className="border-b hover:bg-gray-50 transition">
                  <td className="py-3 px-4 font-medium">{m.name}</td>
                  <td className="py-3 px-4">{purposeCount}</td>
                  <td className="py-3 px-4">{rtCount}</td>
                  <td className="py-3 px-4 text-right">
                    <button
                      className="text-blue-600 hover:text-blue-800 font-medium mr-3"
                      onClick={() => { setEditingMission(m); setShowMissionModal(true); }}
                    >
                      Edit
                    </button>
                    <button
                      className="text-red-500 hover:text-red-700 font-medium"
                      onClick={() => setConfirm({ kind: "mission", id: m.id, name: m.name })}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <table className="min-w-full border-collapse bg-white shadow rounded-xl overflow-hidden">
          <thead>
            <tr className="bg-gray-100 text-left text-gray-600 text-sm uppercase tracking-wider">
              <th className="py-3 px-4">Name</th>
              <th className="py-3 px-4">Result types</th>
              <th className="py-3 px-4">Price</th>
              <th className="py-3 px-4">Custom</th>
              <th className="py-3 px-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.presets.map((p) => (
              <tr key={p.id} className="border-b hover:bg-gray-50 transition">
                <td className="py-3 px-4 font-medium">{p.name}</td>
                <td className="py-3 px-4 text-sm">
                  {(p.resultTypes ?? []).map((rt) => resultTypeHierarchyLabel(rt as ResultTypeDto)).join(", ")}
                </td>
                <td className="py-3 px-4">{p.price}</td>
                <td className="py-3 px-4">{p.custom ? "Yes" : "No"}</td>
                <td className="py-3 px-4 text-right">
                  <button
                    className="text-blue-600 hover:text-blue-800 font-medium mr-3"
                    onClick={() => { setEditingPreset(p); setShowPresetModal(true); }}
                  >
                    Edit
                  </button>
                  <button
                    className="text-red-500 hover:text-red-700 font-medium"
                    onClick={() => setConfirm({ kind: "preset", id: p.id, name: p.name })}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showMissionModal && (
        <MissionModal
          title={editingMission ? `Edit Mission: ${editingMission.name}` : "Create Mission"}
          onClose={() => { setShowMissionModal(false); setEditingMission(undefined); }}
          onSubmit={saveMission}
          mission={editingMission}
          onUploadIcon={editingMission?.id ? (file) => uploadMissionIcon(editingMission.id, file) : undefined}
          onDeleteIcon={editingMission?.id ? () => deleteMissionIcon(editingMission.id) : undefined}
          onUploadPurposeImage={uploadPurposeImage}
          onDeletePurposeImage={deletePurposeImage}
        />
      )}

      {showPresetModal && (
        <PresetModal
          title={editingPreset ? `Edit Preset: ${editingPreset.name}` : "Create Preset"}
          onClose={() => { setShowPresetModal(false); setEditingPreset(undefined); }}
          onSubmit={savePreset}
          preset={editingPreset}
          allResultTypes={allResultTypes}
          onUploadIcon={editingPreset?.id ? (file) => uploadPresetIcon(editingPreset.id, file) : undefined}
          onDeleteIcon={editingPreset?.id ? () => deletePresetIcon(editingPreset.id) : undefined}
        />
      )}

      {confirm && (
        <ConfirmModal
          title="Confirm delete"
          message={`Delete ${confirm.kind} "${confirm.name}"?`}
          onCancel={() => setConfirm(null)}
          onConfirm={async () => {
            const c = confirm;
            setConfirm(null);
            if (c.kind === "mission") await deleteMission(c.id);
            else await deletePreset(c.id);
          }}
        />
      )}
    </div>
  );
};

export default DataManagementPage;

